import React, { FC, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { GenericTable, PortalPopover } from 'components';
import CustomCheckbox from 'components/custom-checkbox/CustomCheckbox';
import { TableColumn } from 'components/generic-table/types';
import {
  BinIcon,
  CheckboxTickIcon,
  DirectionsIcon,
  GreenTickIcon,
  HyphenIcon,
  ListActionsIcon,
  ScheduleJobIcon,
  SetInProgressIcon,
  VerticalListActionsIcon
} from 'assets/icons';

import { JobListItem } from '../jobs.model';
import { getAddressAsString, getJobStatusColor, getJobStatusText } from '../utils';
import DateUtils from 'utils/dateUtils';
import { JobLeadSourceTypes, JobStatus } from '../constant';
import { openInNewTab } from 'utils/commonUtils';
import { GOOGLE_MAPS_BASE_URL } from 'config/index';
import EditScheduleModal from './EditScheduleModal';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { DeleteJobConfirmationModal } from './DeleteJobConfrimationModal';
import { useDeleteJobMutation, useEditJobScheduleMutation } from '../api';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { JobsCleverTapEvents } from '../events';
import { logAnalyticEvent } from 'utils/analytics';
import { PopoverArrowTypes } from 'constants/common';
import DynamicTagsDisplay from './DynamicTagsDisplay';
import { ColumnConfig } from './EditTableModal';
import { API_RESPONSES } from 'types/baseTypes';
import { getInvoiceStatus, getInvoiceStatusColor } from 'containers/invoices/utils';
import { getEstimateStatusColor } from 'containers/estimates/utils';
import { EstimateStatus } from 'containers/estimates/constants';

interface JobsTableProps {
  jobsList?: JobListItem[];
  groupedJobsByStatus?: Record<JobStatus, JobListItem[]>;
  isSelected: (id: string) => boolean;
  onJobClick: (jobId: string) => void;
  page: number;
  fetchData: (page: number) => void;
  jobsCount: number;
  onEditStatusClick: (jobId: string, status: JobStatus, jobNumber: string) => void;
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  currentSortBy?: string;
  currentSortOrder?: string;
  isGroupByStatus?: boolean;
  columnConfig?: ColumnConfig[];
  selectedJobs?: JobListItem[];
  onJobCheckboxSelect?: (job: JobListItem) => void;
  onSelectAll?: () => void;
  onSelectStatusGroup?: (status: JobStatus, jobs: JobListItem[]) => void;
  isSelectAllAcrossPagination?: boolean;
  deselectedJobIds?: string[];
  isJobEffectivelySelected?: (jobId: string) => boolean;
  stickyHorizontalScrollbar?: { bottomOffsetPx: number };
  highlightedRowId?: string | null;
}

const JobsTable: FC<JobsTableProps> = ({
  isSelected: _isSelected,
  jobsList,
  groupedJobsByStatus,
  onJobClick,
  page,
  fetchData,
  onEditStatusClick,
  onSortChange,
  currentSortBy = 'created_at',
  currentSortOrder = 'DESC',
  isGroupByStatus = false,
  columnConfig = [],
  selectedJobs,
  onJobCheckboxSelect,
  onSelectAll,
  onSelectStatusGroup,
  isSelectAllAcrossPagination,
  deselectedJobIds,
  isJobEffectivelySelected,
  stickyHorizontalScrollbar,
  highlightedRowId
}) => {
  const [currentActionsPopoverId, setCurrentActionsPopoverId] = useState<string>(null);
  const [currentJob, setCurrentJob] = useState<JobListItem>(null);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [showDeleteJobConfirmation, setShowDeleteJobConfirmation] = useState(false);

  const currentTeam = useAppSelector(selectCurrentTeam);
  const jobPermissions = useAppSelector(selectCurrentTeamJobPermissions);
  const { modifyDoneJobs } = jobPermissions ?? {};
  const [deleteJobMutation, { isSuccess: isDeleteJobSuccess, isLoading: isDeleteJobLoading }] = useDeleteJobMutation();
  const [editJobScheduleMutation, { isLoading: isEditJobScheduleLoading }] = useEditJobScheduleMutation();
  const canMarkJobDone = jobPermissions?.closeOrMarkJobAsDone;

  useEffect(() => {
    if (!isDeleteJobLoading && isDeleteJobSuccess) {
      setCurrentJob(null);
      setShowDeleteJobConfirmation(false);
      fetchData(page);
    }
  }, [isDeleteJobLoading, isDeleteJobSuccess]);

  const getStatusChangeText = (status: JobStatus) => {
    switch (status) {
      case JobStatus.INBOX:
        return (
          <>
            <ScheduleJobIcon />
            <div>Submit job</div>
          </>
        );
      case JobStatus.SUBMITTED:
        return (
          <>
            <ScheduleJobIcon />
            <div>Confirm job</div>
          </>
        );
      case JobStatus.CONFIRMED:
      case JobStatus.PENDING:
        return (
          <>
            <SetInProgressIcon />
            <div>Set as in progress</div>
          </>
        );
      case JobStatus.IN_PROGRESS:
        if (canMarkJobDone) {
          return (
            <>
              <GreenTickIcon />
              <div>Mark job as done</div>
            </>
          );
        }

        return (
          <>
            <GreenTickIcon />
            <div>Mark job as done pending approval</div>
          </>
        );

      default:
        return null;
    }
  };

  const onStatusChange = (job: JobListItem) => {
    switch (job.status) {
      case JobStatus.INBOX:
        return onEditStatusClick(job.id, JobStatus.SUBMITTED, job.jobNumber);
      case JobStatus.SUBMITTED:
        return onEditStatusClick(job.id, JobStatus.CONFIRMED, job.jobNumber);

      case JobStatus.CONFIRMED:
      case JobStatus.PENDING:
        return onEditStatusClick(job.id, JobStatus.IN_PROGRESS, job.jobNumber);

      case JobStatus.IN_PROGRESS:
        if (canMarkJobDone) {
          return onEditStatusClick(job.id, JobStatus.DONE, job.jobNumber);
        }
        return onEditStatusClick(job.id, JobStatus.DONE_PENDING_APPROVAL, job.jobNumber);
      default:
        return null;
    }
  };

  const canShowDelete = (jobStatus: JobStatus, isEditable: boolean) => {
    if (!isEditable) {
      return false;
    }
    if (jobStatus === JobStatus.DONE) {
      return modifyDoneJobs;
    }
    return jobStatus !== JobStatus.REJECTED;
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListItemActionsMenu: FC<{ job: JobListItem }> = ({ job }) => {
    return (
      <div className="space-y-3 text-sbase font-semibold text-primaryText">
        {job.status !== JobStatus.DONE && job.status !== JobStatus.REJECTED && job?.isEditable && (
          <div
            id="jobs-table-3dot-share-job"
            className={'flex flex-row items-center gap-2'}
            onClick={e => {
              e.stopPropagation();
              onStatusChange(job);
              setCurrentActionsPopoverId(null);
            }}>
            {getStatusChangeText(job.status)}
          </div>
        )}
        <div
          id="jobs-table-3dot-show-directions"
          onClick={e => {
            e.stopPropagation();
            openInNewTab(`${GOOGLE_MAPS_BASE_URL}${getAddressAsString(job.customer.address)}`);
            setCurrentActionsPopoverId(null);
          }}
          className={'flex flex-row items-center gap-2'}>
          <DirectionsIcon />
          <div>Show directions</div>
        </div>
        {canShowDelete(job.status, job?.isEditable) && (
          <div
            id="jobs-table-3dot-delete-job"
            onClick={e => {
              e.stopPropagation();
              setCurrentJob(job);
              setShowDeleteJobConfirmation(true);
            }}
            className={'flex flex-row items-center gap-2 '}>
            <BinIcon />
            <div>Delete</div>
          </div>
        )}
      </div>
    );
  };

  // eslint-disable-next-line react/no-multi-comp
  const ListActionsAnchor: React.FC<{ isPanelOpen: boolean }> = ({ isPanelOpen }) => {
    return (
      <div id="jobs-table-more-options" className="rounded-full p-2 hover:bg-gray-100">
        <ListActionsIcon
          className={twMerge(
            'hidden shrink-0 lg:flex',
            classnames({
              'fill-current text-secondary': isPanelOpen
            })
          )}
        />
        <VerticalListActionsIcon
          className={twMerge(
            'mx-2 flex shrink-0 lg:hidden',
            classnames({
              'fill-current text-secondary': isPanelOpen
            })
          )}
        />
      </div>
    );
  };

  const onToggleListItemActions = (jobId: string) => {
    if (jobId === currentActionsPopoverId) {
      setCurrentActionsPopoverId(null);
    } else {
      setCurrentActionsPopoverId(jobId);
    }
  };

  const isListItemActionsVisible = (jobId: string) => {
    return jobId === currentActionsPopoverId;
  };

  const onCloseListItemActionsPopover = (jobId: string) => {
    if (currentActionsPopoverId === jobId) {
      setCurrentActionsPopoverId(null);
    }
  };

  const handleSaveScheduleClick = (scheduledFrom: string, scheduledTo: string) => {
    editJobScheduleMutation({
      scheduledFrom,
      scheduledTo,
      jobId: currentJob.id,
      teamId: currentTeam.id
    }).then(response => {
      if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
        setOpenScheduleModal(false);
        fetchData(page);
      }
    });
  };

  const onDeleteJob = () => {
    deleteJobMutation({ jobId: currentJob.id, teamId: currentTeam.id });
    logAnalyticEvent(JobsCleverTapEvents.webDeleteJobConfirmation);
  };

  const handleJobClick = (jobId: string) => {
    setCurrentActionsPopoverId('');
    onJobClick(jobId);
  };

  const getStatusDate = (status: JobStatus, createdAt: string, updatedAt: string) => {
    if (status === JobStatus.SUBMITTED || status === JobStatus.CONFIRMED || status === JobStatus.INBOX) {
      return `Created on ${DateUtils.getDateInFormat({ date: new Date(createdAt), dateFormat: 'dd MMM yyyy' })}`;
    }

    return `Updated on  ${DateUtils.getDateInFormat({ date: new Date(updatedAt), dateFormat: 'dd MMM yyyy' })}`;
  };

  const tableData = useMemo(() => {
    if (isGroupByStatus && groupedJobsByStatus) {
      return Object.values(groupedJobsByStatus).flat();
    }
    return jobsList ?? [];
  }, [isGroupByStatus, groupedJobsByStatus, jobsList]);

  const genericColumnInfo = useMemo(
    () =>
      columnConfig?.map(c => ({
        columnKey: c.columnKey,
        isEnabled: c.isEnabled,
        priority: c.priority
      })),
    [columnConfig]
  );

  // Kept for potential future group-by-status UI in GenericTable
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _getStatusGroupCheckboxIcon = (statusJobs: JobListItem[]) => {
    if (statusJobs.length === 0) return null;

    const statusJobIds = statusJobs.map(job => job.id);

    // If select all across pagination is active, check effective selection
    if (isSelectAllAcrossPagination) {
      const statusJobsEffectivelySelected = statusJobIds.filter(jobId => !deselectedJobIds?.includes(jobId));
      const areAllStatusJobsSelected = statusJobIds.length === statusJobsEffectivelySelected.length;

      if (areAllStatusJobsSelected) {
        return <CheckboxTickIcon className="h-3 w-3 text-white" />;
      }
      if (statusJobsEffectivelySelected.length > 0) {
        return <HyphenIcon className="h-3 w-3 text-white" />;
      }
      return null;
    }

    // Regular selection logic
    const selectedStatusJobs = selectedJobs?.filter(job => statusJobIds.includes(job.id)) || [];
    const areAllStatusJobsSelected = statusJobIds.length === selectedStatusJobs.length;

    if (areAllStatusJobsSelected) {
      return <CheckboxTickIcon className="h-3 w-3 text-white" />;
    }
    if (selectedStatusJobs.length > 0) {
      return <HyphenIcon className="h-3 w-3 text-white" />;
    }

    return null;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleSelectStatusGroup = (status: JobStatus, statusJobs: JobListItem[]) => {
    if (onSelectStatusGroup) {
      onSelectStatusGroup(status, statusJobs);
    }
  };

  // Define the new columns based on the image
  const columns: TableColumn<JobListItem>[] = useMemo(
    () => [
      {
        id: 'checkbox',
        header: ({ selectedItems, allItems, onSelectAll: onSelectAllClick }) => {
          const isAllSelected = (allItems?.length ?? 0) > 0 && (selectedItems?.length ?? 0) === allItems?.length;
          const isSomeSelected = (selectedItems?.length ?? 0) > 0;
          let headerIcon: React.ReactNode = null;
          if (isAllSelected) headerIcon = <CheckboxTickIcon className="h-3 w-3 text-white" />;
          else if (isSomeSelected) headerIcon = <HyphenIcon className="h-3 w-3 text-white" />;
          return (
            <div className="flex cursor-pointer items-center gap-6 pl-[14px]">
              <CustomCheckbox
                id="select-all-jobs"
                className={isSomeSelected || isAllSelected ? '' : 'bg-white'}
                isChecked={isSomeSelected || isAllSelected}
                onClick={() => onSelectAllClick?.()}>
                {headerIcon}
              </CustomCheckbox>
            </div>
          );
        },
        accessor: data => (
          <div
            className="flex cursor-pointer items-center gap-6 pl-[14px]"
            onClick={e => {
              e.stopPropagation();
              data.onItemSelect?.(data);
            }}>
            <CustomCheckbox
              className="shrink-0"
              id={`select-job-${data.id}`}
              isChecked={data.selectedItems?.some((job: JobListItem) => job.id === data.id)}
              onClick={() => data.onItemSelect?.(data)}>
              {data.selectedItems?.some((job: JobListItem) => job.id === data.id) && (
                <CheckboxTickIcon className="h-3 w-3 text-white" />
              )}
            </CustomCheckbox>
          </div>
        ),
        width: 'w-[64px]'
      },
      {
        id: 'jobNumber',
        header: 'Job ID',
        sortable: true,
        accessor: (job: JobListItem) => (
          <span className="text-sbase font-semibold text-primaryText">{job.jobNumber.replaceAll('JOB-', '')}</span>
        ),
        width: 'w-[120px] min-w-[120px] max-w-[160px]'
      },
      {
        id: 'jobTags',
        header: 'Tags',
        accessor: (job: JobListItem) => <DynamicTagsDisplay job={job} />,
        width: 'w-[146px] min-w-[146px] max-w-[146px]'
      },
      {
        id: 'customerName',
        header: 'Customer',
        sortable: true,
        accessor: (job: JobListItem) => (
          <div className="flex flex-col gap-1 text-sbase text-primaryText">
            <span className="">{job.customer.name}</span>
            {job.customer.companyName && <span className="">{job.customer.companyName}</span>}
            {job.customer.address && <div className="text-accentText">{getAddressAsString(job.customer.address)}</div>}
          </div>
        ),
        width: 'w-[200px] min-w-[180px] max-w-[240px]'
      },
      {
        id: 'title',
        header: 'Job title',
        accessor: (job: JobListItem) => (
          <span className="break-words text-sbase font-semibold text-primaryText">{job.title}</span>
        ),
        width: 'w-[160px] min-w-[120px] max-w-[200px]'
      },
      {
        id: 'scheduledFrom',
        header: 'Schedule',
        accessor: (job: JobListItem) => (
          <div className="flex flex-col gap-1 text-sbase text-primaryText">
            {job.scheduledFrom ? (
              <>
                <div className="whitespace-nowrap">
                  {`${DateUtils.getDayOfWeek(job.scheduledFrom)}, ${DateUtils.getDateInFormat({
                    date: new Date(job.scheduledFrom),
                    dateFormat: 'dd MMM yyyy'
                  })}`}
                </div>
                <div>{`${DateUtils.formatTime(job.scheduledFrom)} - ${DateUtils.formatTime(job.scheduledTo)}`}</div>
                <div className="w-max rounded bg-secondaryBtn py-0.5 px-1.5 text-px13 text-accentText">
                  {DateUtils.getDaysDifference(new Date(job.scheduledFrom))}
                </div>
              </>
            ) : (
              <div className="whitespace-nowrap text-accentText">Not yet confirmed</div>
            )}
          </div>
        ),
        width: 'w-[180px] min-w-[140px] max-w-[220px]'
      },
      {
        id: 'status',
        header: 'Status',
        accessor: (job: JobListItem) => (
          <div className="min-w-[100px] max-w-[180px] text-sbase text-primaryText">
            <div className={'text-sbase font-bold'} style={{ color: getJobStatusColor(job.status) }}>
              {getJobStatusText(job.status)}
            </div>
            <div className="mt-1 text-[13px] text-accentText">
              {getStatusDate(job.status, job.createdAt, job.updatedAt)}
            </div>
          </div>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[160px]'
      },
      {
        id: 'invoiceStatus',
        header: 'Invoice status',
        accessor: (job: JobListItem) => (
          <div className="min-w-[100px] max-w-[180px] text-sbase text-primaryText">
            <div
              className={'text-sbase font-bold'}
              style={job.invoiceStatus ? { color: getInvoiceStatusColor(job.invoiceStatus) } : {}}>
              {job.invoiceStatus ? getInvoiceStatus(job.invoiceStatus) : '-'}
            </div>
          </div>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[160px]'
      },
      {
        id: 'estimateStatus',
        header: 'Estimate status',
        accessor: (job: JobListItem) => (
          <div className="min-w-[100px] max-w-[180px] text-sbase text-primaryText">
            <div
              className={'text-sbase font-bold capitalize'}
              style={job.estimateStatus ? { color: getEstimateStatusColor(job.estimateStatus as EstimateStatus) } : {}}>
              {job.estimateStatus ? job.estimateStatus?.toLowerCase() : '-'}
            </div>
          </div>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[160px]'
      },
      {
        id: 'externalCompanyName',
        header: 'External company',
        accessor: (job: JobListItem) => (
          <span className="text-sbase text-primaryText">{job.externalCompanyName || '-'}</span>
        ),
        width: 'w-[160px] min-w-[160px] max-w-[180px]'
      },
      {
        id: 'assignees',
        header: 'Team',
        accessor: (job: JobListItem) => (
          <div className="flex flex-col gap-1">
            {job.assignees?.length > 0 ? (
              job.assignees.map(member => (
                <div key={member.id} className="text-sbase text-primaryText ">
                  <span>{member.name}</span>
                </div>
              ))
            ) : (
              <div className="text-sbase text-primaryText">-</div>
            )}
          </div>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[180px]'
      },
      {
        id: 'serviceAreaName',
        header: 'Service area',
        accessor: (job: JobListItem) => (
          <span className="text-sbase text-primaryText">{job.serviceAreaName || '-'}</span>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[180px]'
      },
      {
        id: 'amount',
        header: 'Amount',
        accessor: (job: JobListItem) => (
          <span className="text-sbase font-semibold text-primaryText">{getAmountWithCurrency(job.amount)}</span>
        ),
        width: 'w-[120px] min-w-[100px] max-w-[160px]'
      },
      {
        id: 'leadSourceType',
        header: 'Lead source',
        accessor: (job: JobListItem) => (
          <span className="text-sbase font-semibold text-primaryText">
            {JobLeadSourceTypes.find(type => type.value === job.leadSourceType)?.label || '-'}
            {job.leadSource && <span className="text-accentText"> - {job.leadSource}</span>}
          </span>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[180px]'
      },
      {
        id: 'createdBy',
        header: 'Created by',
        accessor: (job: JobListItem) => (
          <span className="text-sbase font-semibold text-primaryText">{job.createdBy?.name || '-'}</span>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[180px]'
      },
      {
        id: 'customerAddressStreet',
        header: 'Street address',
        accessor: (job: JobListItem) => (
          <span className="text-sbase text-primaryText">{job.customer?.address?.street || '-'}</span>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[180px]'
      },
      {
        id: 'customerAddressCity',
        header: 'City',
        accessor: (job: JobListItem) => (
          <span className="text-sbase text-primaryText">{job.customer?.address?.city || '-'}</span>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[180px]'
      },
      {
        id: 'customerAddressState',
        header: 'State',
        accessor: (job: JobListItem) => (
          <span className="text-sbase text-primaryText">{job.customer?.address?.state || '-'}</span>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[180px]'
      },
      {
        id: 'customerAddressZipCode',
        header: 'Zipcode',
        accessor: (job: JobListItem) => (
          <span className="text-sbase text-primaryText">{job.customer?.address?.zipCode || '-'}</span>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[180px]'
      },
      {
        id: 'createdAt',
        header: 'Created On',
        accessor: (job: JobListItem) => (
          <span className="text-sbase text-primaryText">
            {DateUtils.getDateInFormat({ date: new Date(job.createdAt), dateFormat: 'dd MMM yyyy' })}
          </span>
        ),
        width: 'w-[140px] min-w-[120px] max-w-[180px]'
      },
      {
        id: 'actions',
        header: '',
        accessor: (job: JobListItem) => (
          <PortalPopover
            anchorComponent={ListActionsAnchor}
            show={isListItemActionsVisible(job.id)}
            onTogglePopover={() => onToggleListItemActions(job.id)}
            onClosePopover={() => onCloseListItemActionsPopover(job.id)}
            offset={{ right: 40, top: -20 }}
            width="w-[200px]"
            minWidth="min-w-[200px]"
            padding="p-4"
            showArrow={true}
            arrowType={PopoverArrowTypes.RIGHT}
            highlightAnchor={false}
            containerClassName="border border-borderGray">
            <ListItemActionsMenu job={job} />
          </PortalPopover>
        ),
        width: 'w-[80px] min-w-[80px] max-w-[100px]',
        fixed: 'right'
      }
    ],
    [currentActionsPopoverId]
  );

  const effectiveSelectedItems = useMemo(
    () => tableData.filter(j => isJobEffectivelySelected?.(j.id)),
    [tableData, isJobEffectivelySelected]
  );

  return (
    <>
      <GenericTable<JobListItem>
        data={tableData}
        columns={columns}
        columnInfo={genericColumnInfo}
        showCheckbox={true}
        sorting={
          onSortChange
            ? {
                sortBy: currentSortBy,
                sortOrder: (currentSortOrder ?? 'DESC').toLowerCase(),
                onSortChange: (sortBy, order) => onSortChange(sortBy, order.toUpperCase())
              }
            : undefined
        }
        selection={{
          selectedItems: effectiveSelectedItems,
          onItemSelect: onJobCheckboxSelect,
          onSelectAll,
          getItemId: j => j.id,
          allItems: tableData
        }}
        onRowClick={item => handleJobClick(item.id)}
        stickyHorizontalScrollbar={stickyHorizontalScrollbar}
      />
      {openScheduleModal && (
        <EditScheduleModal
          onClose={() => setOpenScheduleModal(false)}
          onSave={handleSaveScheduleClick}
          scheduledFrom={currentJob.scheduledFrom}
          scheduledTo={currentJob.scheduledTo}
          isScheduled={!!currentJob.scheduledFrom}
          isLoading={isEditJobScheduleLoading}
        />
      )}
      {showDeleteJobConfirmation && (
        <DeleteJobConfirmationModal
          onCloseClick={() => setShowDeleteJobConfirmation(false)}
          handleDelete={onDeleteJob}
          processingDelete={isDeleteJobLoading}
        />
      )}
    </>
  );
};

export default JobsTable;
